import { useCallback, useEffect, useMemo, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { HubCallback } from '@aws-amplify/core';
import { ApplicationAuthConfig, ProvisionerTask } from '@tanium-cloud/tsp-contracts';
import { useCognito } from './useCognito';
import { CognitoAuthenticatedUser } from '.';
import { SessionState } from '../session';
import { CognitoErrors, isCognitoError } from './CognitoErrors';
import { getApiClient } from '../apiClient';
import { useAsyncTaskCallback } from '../hooks/useAsyncTask';
import { parseAxiosError } from '../utilities/commonFuncs';

interface CognitoAuthResponse extends CognitoUser {
  challengeName?: string;
  challengeParam?: NewPasswordChallengeParam | any;
}

interface NewPasswordChallengeParam {
  requiredAttributes: string[];
}

export enum AuthState {
  LOADING = 'loading',
  SIGNED_IN = 'signed_in',
  SIGN_IN = 'sign_in',
  SMS_MFA = 'sms_mfa',
  SOFTWARE_TOKEN_MFA = 'software_token_mfa',
  NEW_PASSWORD_REQUIRED = 'new_password_required',
  MFA_SETUP = 'mfa_setup',
  FORGOT_PASSWORD = 'forgot_password',
  RESET_PASSWORD = 'reset_password',
  VERIFY_CONTACT = 'verify_contact',
  INVALID_CHALLENGE = 'invalid_challenge',
}

/** Authentication challenges presented by Cognito during the sign in flow */
export enum AuthChallenge {
  SMS_MFA = 'SMS_MFA',
  SOFTWARE_TOKEN_MFA = 'SOFTWARE_TOKEN_MFA',
  NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED',
  MFA_SETUP = 'MFA_SETUP',
}

export interface CognitoAuth {
  session: SessionState | null;
  authState: AuthState;
  loginUsername: string | null;
  idpEnabled: boolean;
  localEnabled: boolean;
  isGov?: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signInIdp: (username: string) => Promise<void>;
  submitNewPassword: (password: string) => Promise<void>;
  generateTotpSecret: () => Promise<string>;
  completeTotpSetup: (code: string) => Promise<void>;
  confirmTotpCode: (code: string) => Promise<void>;
  startResetPasswordWorkFlow: () => void;
  requestOTPCode: (username: string) => Promise<void>;
  resetPassword: (payload: { emailAddress: string; otpCode: string }) => Promise<ProvisionerTask>;
  resetAuthState: () => void;
  signOut: () => Promise<void>;
}

const getAuthStateFromChallenge = (challengeName: string): AuthState => {
  switch (challengeName) {
    case AuthChallenge.SMS_MFA:
      return AuthState.SMS_MFA;
    case AuthChallenge.SOFTWARE_TOKEN_MFA:
      return AuthState.SOFTWARE_TOKEN_MFA;
    case AuthChallenge.NEW_PASSWORD_REQUIRED:
      return AuthState.NEW_PASSWORD_REQUIRED;
    case AuthChallenge.MFA_SETUP:
      return AuthState.MFA_SETUP;
    default:
      return AuthState.INVALID_CHALLENGE;
  }
};

const getSessionState = (
  authenticatedUser: CognitoAuthenticatedUser | null,
  endSession: () => Promise<void>
): SessionState | null => {
  const userSession = authenticatedUser?.getSignInUserSession();
  if (!userSession?.isValid()) {
    return null;
  }
  const username = authenticatedUser?.attributes?.email ?? '';
  return {
    currentUser: { name: username },
    endSession,
  };
};

const extractEmailDomain = (email: string): string => {
  if (email.lastIndexOf('@') === -1) {
    throw new Error('Invalid email format');
  }
  return email.toLowerCase().substring(email.lastIndexOf('@') + 1);
};

export const useCognitoAuth = (
  authConfig: ApplicationAuthConfig,
  onTokenRefresh: () => void,
  onSignOut: () => void
): CognitoAuth => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [authState, setAuthState] = useState(AuthState.LOADING);
  // cache username for login challenge flow
  const [loginUsername, setLoginUsername] = useState<string | null>(null);

  useCognito(authConfig);

  const resetAuthState = useCallback(() => {
    setUser(null);
    setLoginUsername(null);
    setAuthState(AuthState.SIGN_IN);
  }, []);

  const updateAuthState = useCallback(
    async (currentUser: CognitoAuthResponse | null): Promise<void> => {
      if (!currentUser) {
        // initial auth state
        resetAuthState();
        return;
      }

      if (currentUser.getSignInUserSession()?.isValid()) {
        const signedInUser = (await Auth.currentAuthenticatedUser()) as CognitoAuthResponse;
        setUser(signedInUser);
        setAuthState(AuthState.SIGNED_IN);
        return;
      }

      if (currentUser.challengeName) {
        const challengeAuthState = getAuthStateFromChallenge(currentUser.challengeName);
        setUser(currentUser);
        setAuthState(challengeAuthState);
        return;
      }

      // current user session is invalid so return user to sign in page
      resetAuthState();
    },
    [resetAuthState, setUser, setAuthState]
  );

  useEffect(() => {
    async function getUser() {
      try {
        const currentUser = (await Auth.currentAuthenticatedUser()) as CognitoAuthResponse;
        await updateAuthState(currentUser);
      } catch (err) {
        // currentAuthenticatedUser throws when user is not authenticated
        resetAuthState();
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getUser();
  }, [updateAuthState, resetAuthState]);

  const signIn = useCallback(
    async (rawUsername: string, password: string) => {
      const username = rawUsername.toLowerCase();
      try {
        const authResponse = (await Auth.signIn({ username, password })) as CognitoAuthResponse | null;
        setLoginUsername(username);
        await updateAuthState(authResponse);
      } catch (err) {
        if (!isCognitoError(err)) throw err;
        if (err.code === CognitoErrors.PasswordResetRequiredException) {
          setUser(null);
          setAuthState(AuthState.FORGOT_PASSWORD);
        } else {
          throw err;
        }
      }
    },
    [updateAuthState, setLoginUsername, setUser, setAuthState]
  );

  const signInIdp = useCallback(async (username: string) => {
    await Auth.federatedSignIn({
      customProvider: extractEmailDomain(username),
    });
  }, []);

  const submitNewPassword = useCallback(
    async (password: string) => {
      try {
        const authResponse = (await Auth.completeNewPassword(user, password)) as CognitoAuthResponse;
        await updateAuthState(authResponse);
      } catch (err) {
        if (!isCognitoError(err)) throw err;
        if (err.code === CognitoErrors.NotAuthorizedException) {
          resetAuthState();
        } else if (err.code === CognitoErrors.PasswordResetRequiredException) {
          setUser(null);
          setAuthState(AuthState.FORGOT_PASSWORD);
        } else {
          throw err;
        }
      }
    },
    [resetAuthState, updateAuthState, user, setUser, setAuthState]
  );

  const generateTotpSecret = useCallback(async () => {
    const secret = await Auth.setupTOTP(user);
    return secret;
  }, [user]);

  const completeTotpSetup = useCallback(
    async (code: string) => {
      try {
        if (user) {
          await Auth.verifyTotpToken(user, code);
          await updateAuthState(user);
        }
      } catch (err) {
        if (!isCognitoError(err)) throw err;
        if (err.code === CognitoErrors.NotAuthorizedException) {
          resetAuthState();
        } else if (err.code === CognitoErrors.PasswordResetRequiredException) {
          setUser(null);
          setAuthState(AuthState.FORGOT_PASSWORD);
        } else {
          throw err;
        }
      }
    },
    [resetAuthState, updateAuthState, user, setUser, setAuthState]
  );

  const confirmTotpCode = useCallback(
    async (code: string) => {
      try {
        if (user) {
          const authResponse = (await Auth.confirmSignIn(user, code, 'SOFTWARE_TOKEN_MFA')) as CognitoAuthResponse;
          await updateAuthState(authResponse);
        }
      } catch (err) {
        if (!isCognitoError(err)) throw err;
        if (err.code === CognitoErrors.NotAuthorizedException) {
          resetAuthState();
        } else if (err.code === CognitoErrors.PasswordResetRequiredException) {
          setUser(null);
          setAuthState(AuthState.FORGOT_PASSWORD);
        } else {
          throw err;
        }
      }
    },
    [resetAuthState, updateAuthState, user, setUser, setAuthState]
  );

  const startResetPasswordWorkFlow = useCallback(() => {
    setUser(null);
    setAuthState(AuthState.FORGOT_PASSWORD);
  }, []);

  const apiClient = getApiClient();
  const requestOTPCode = useCallback(
    async (emailAddress: string) => {
      try {
        await apiClient.put('/api-public/credentials/otp-code/', { emailAddress });
      } catch (err: any) {
        throw parseAxiosError(err);
      }
    },
    [apiClient]
  );

  const resetPassword = useAsyncTaskCallback('/api-public/credentials/reset-password/');

  const signOut = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    await Auth.signOut();
    resetAuthState();
  }, [resetAuthState]);

  const session = useMemo(() => getSessionState(user, signOut), [signOut, user]);

  useEffect(() => {
    const authListener: HubCallback = ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh_failure':
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          signOut();
          break;
        case 'tokenRefresh':
          onTokenRefresh();
          break;
        case 'signOut':
          onSignOut();
          break;
      }
    };

    Hub.listen('auth', authListener);
    return () => {
      Hub.remove('auth', authListener);
    };
  }, [signOut, onTokenRefresh, onSignOut]);

  return {
    session,
    authState,
    loginUsername,
    idpEnabled: authConfig.idpEnabled,
    localEnabled: authConfig.localEnabled,
    isGov: authConfig.isGov,
    signIn,
    signInIdp,
    submitNewPassword,
    generateTotpSecret,
    completeTotpSetup,
    confirmTotpCode,
    startResetPasswordWorkFlow,
    requestOTPCode,
    resetPassword,
    resetAuthState,
    signOut,
  };
};
